import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SisSelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

let sisSelectId = 0;

@Component({
  selector: 'app-sis-select',
  templateUrl: './sis-select.component.html',
  styleUrls: ['./sis-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SisSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class SisSelectComponent implements ControlValueAccessor, OnDestroy {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() helper = '';
  @Input() error = '';
  @Input() options: SisSelectOption[] = [];
  @Input() loading = false;
  @Input() loadedKey: unknown = null;
  @Input() loadedMessage = '';
  @Input() loadFailed = false;
  @Input() showRetry = true;
  @Output() retryLoad = new EventEmitter<void>();
  @Input() caption = '';
  @Input() busy = false;

  readonly selectId = `sis-select-${++sisSelectId}`;
  value = '';
  // El desplegable nativo no expone eventos de apertura, así que seguimos las
  // interacciones que lo abren/cierran para animar la flecha. Si el estado se
  // desincronizara, el blur/change siguiente lo devuelve a cerrado.
  pickerOpen = false;
  @Input() disabled = false;
  // Form-independent lock (see SisFieldComponent.locked): disables the native
  // select for interaction/visual while keeping its value in the reactive form.
  @Input() locked = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private teardownPickerWatchers: (() => void) | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  get describedBy(): string | null {
    const ids = [];
    if (this.helperText) {
      ids.push(`${this.selectId}-helper`);
    }
    if (this.error) {
      ids.push(`${this.selectId}-error`);
    }
    return ids.length ? ids.join(' ') : null;
  }

  get helperText(): string {
    if (this.error) {
      return this.error;
    }

    if (this.loadFailed) {
      return 'No se pudo cargar';
    }

    if (this.helper) {
      return this.helper;
    }

    if (this.caption) {
      return this.caption;
    }

    return '';
  }

  get effectiveLoading(): boolean {
    return this.loading || this.busy;
  }

  get effectiveDisabled(): boolean {
    return this.disabled || this.effectiveLoading;
  }

  get isLockedOrDisabled(): boolean {
    return this.effectiveDisabled || this.locked;
  }

  get helperFailed(): boolean {
    return !this.error && this.loadFailed;
  }

  writeValue(value: unknown): void {
    this.value = value == null ? '' : String(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelect(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.value = select.value;
    this.setPickerOpen(false);
    this.onChange(this.value);
  }

  onBlur(): void {
    this.setPickerOpen(false);
    this.onTouched();
  }

  // El click llega al propio select (incluida el área de la flecha, que solo es
  // decorativa), así que el navegador ya abrió/cerró el desplegable.
  onTogglePicker(): void {
    if (this.isLockedOrDisabled) {
      return;
    }
    this.setPickerOpen(!this.pickerOpen);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Tab' || event.key === 'Enter') {
      this.setPickerOpen(false);
      return;
    }

    if (event.altKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      this.setPickerOpen(!this.pickerOpen);
    }
  }

  ngOnDestroy(): void {
    this.teardownPickerWatchers?.();
  }

  private setPickerOpen(open: boolean): void {
    if (this.pickerOpen === open) {
      return;
    }

    this.pickerOpen = open;
    this.teardownPickerWatchers?.();
    this.teardownPickerWatchers = open ? this.watchForPickerDismiss() : null;
  }

  // El desplegable nativo no avisa cuando se cierra y, al descartarlo con un
  // click fuera, el navegador se come ese click (no llega ni pointerdown ni
  // blur), así que la flecha se quedaba girada. Mientras está abierto la página
  // tampoco recibe eventos de puntero: el primero que llegue significa que el
  // desplegable ya se cerró.
  private watchForPickerDismiss(): () => void {
    const close = (event?: Event) => {
      // Un pointerdown sobre el propio select ya lo maneja onTogglePicker.
      if (event && this.host.nativeElement.contains(event.target as Node)) {
        return;
      }
      this.zone.run(() => {
        this.pickerOpen = false;
        this.teardownPickerWatchers?.();
        this.teardownPickerWatchers = null;
        this.cdr.markForCheck();
      });
    };
    const onWindowBlur = () => close();

    return this.zone.runOutsideAngular(() => {
      document.addEventListener('pointerdown', close, true);
      document.addEventListener('pointermove', close, true);
      window.addEventListener('blur', onWindowBlur);

      return () => {
        document.removeEventListener('pointerdown', close, true);
        document.removeEventListener('pointermove', close, true);
        window.removeEventListener('blur', onWindowBlur);
      };
    });
  }
}
